import { DateTime } from 'luxon';

import { transportStates } from './types';

import type {
  PhaserAttributes,
  TransportResult,
  TransportState,
} from './types';

export const setPhaserAttributesToUrl = (
  urlArg: string | URL,
  attributes: PhaserAttributes,
): URL => {
  const url = removePhaseAttributesFromUrl(urlArg);

  if (typeof attributes.datetime !== 'undefined') {
    url.searchParams.set('phaser[datetime]', attributes.datetime);
  }

  if (attributes.mockInternetCard === true) {
    url.searchParams.set('phaser[mockInternetCard]', '1');
  }

  if (typeof attributes.vip !== 'undefined') {
    url.searchParams.set('phaser[vip]', attributes.vip.toString());
  }

  if (typeof attributes.tariff !== 'undefined') {
    url.searchParams.set('phaser[tariff]', attributes.tariff);
  }

  if (typeof attributes.mpi !== 'undefined') {
    url.searchParams.set('phaser[mpi]', attributes.mpi.toString());
  }

  if (typeof attributes.cabinNumber !== 'undefined') {
    url.searchParams.set('phaser[cabinNumber]', attributes.cabinNumber);
  }

  if (typeof attributes.shipCode !== 'undefined') {
    url.searchParams.set('phaser[shipCode]', attributes.shipCode);
  }

  if (typeof attributes.bus !== 'undefined') {
    url.searchParams.set('phaser[bus]', attributes.bus.toString());
  }

  if (typeof attributes.trainVariant !== 'undefined') {
    url.searchParams.set(
      'phaser[trainVariant]',
      attributes.trainVariant.toString(),
    );
  }

  if (typeof attributes.flightVariant !== 'undefined') {
    url.searchParams.set(
      'phaser[flightVariant]',
      attributes.flightVariant.toString(),
    );
  }

  if (typeof attributes.isDebugMode !== 'undefined') {
    url.searchParams.set(
      'phaser[isDebugMode]',
      attributes.isDebugMode.toString(),
    );
  }

  if (typeof attributes.isEmptyStateForced !== 'undefined') {
    url.searchParams.set(
      'phaser[isEmptyStateForced]',
      attributes.isEmptyStateForced.toString(),
    );
  }

  return url;
};

export const removePhaseAttributesFromUrl = (urlArg: string | URL): URL => {
  const url = new URL(urlArg);

  url.searchParams.delete('phaser[datetime]');
  url.searchParams.delete('phaser[mockInternetCard]');
  url.searchParams.delete('phaser[vip]');
  url.searchParams.delete('phaser[tariff]');
  url.searchParams.delete('phaser[mpi]');
  url.searchParams.delete('phaser[cabinNumber]');
  url.searchParams.delete('phaser[shipCode]');
  url.searchParams.delete('phaser[flight]');
  url.searchParams.delete('phaser[bus]');
  url.searchParams.delete('phaser[trainVariant]');
  url.searchParams.delete('phaser[flightVariant]');
  url.searchParams.delete('phaser[isDebugMode]');
  url.searchParams.delete('phaser[isEmptyStateForced]');

  return url;
};

export const getPhaserAttributesFromUrl = (
  urlArg: string | URL,
): PhaserAttributes => {
  return getPhaserAttributesFromSearchParams(new URL(urlArg).searchParams);
};

export const getPhaserAttributesFromObject = (
  attrs: Record<string, string | undefined>,
): PhaserAttributes => {
  return castPhaserAttributes(attrs);
};

export const getPhaserAttributesFromSearchParams = (
  paramsArg: URLSearchParams | Record<string, string>,
): PhaserAttributes => {
  const params = new URLSearchParams(paramsArg);

  return castPhaserAttributes({
    datetime: params.get('phaser[datetime]'),
    mockInternetCard: params.get('phaser[mockInternetCard]'),
    vip: params.get('phaser[vip]'),
    tariff: params.get('phaser[tariff]'),
    mpi: params.get('phaser[mpi]'),
    cabinNumber: params.get('phaser[cabinNumber]'),
    shipCode: params.get('phaser[shipCode]'),
    bus: params.get('phaser[bus]'),
    trainVariant: params.get('phaser[trainVariant]'),
    flightVariant: params.get('phaser[flightVariant]'),
    isDebugMode: params.get('phaser[isDebugMode]'),
    isEmptyStateForced: params.get('phaser[isEmptyStateForced]'),
  });
};

const castPhaserAttributes = (
  attrs: Record<keyof PhaserAttributes, unknown>,
): PhaserAttributes => {
  const datetime = toDateTimeString(attrs.datetime);
  const mockInternetCard = toBoolean(attrs.mockInternetCard);
  const vip = toNumber(attrs.vip);
  const tariff = toString(attrs.tariff);
  const mpi = toNumber(attrs.mpi);
  const cabinNumber = toString(attrs.cabinNumber);
  const shipCode = toString(attrs.shipCode);
  const bus = toBoolean(attrs.bus);
  const trainVariant = toNumber(attrs.trainVariant);
  const flightVariant = toNumber(attrs.flightVariant);
  const isDebugMode = toBoolean(attrs.isDebugMode);
  const isEmptyStateForced = toBoolean(attrs.isEmptyStateForced);

  return {
    datetime,
    mockInternetCard,
    vip,
    tariff,
    mpi,
    cabinNumber,
    shipCode,
    bus,
    trainVariant,
    flightVariant,
    isDebugMode,
    isEmptyStateForced,
  };
};

export const phaserAttributesSet = (attrs: PhaserAttributes | undefined) => {
  if (typeof attrs === 'undefined') {
    return false;
  }

  const first = Object.values(attrs).find(value => {
    return typeof value !== 'undefined' && value !== null;
  });

  return typeof first !== 'undefined';
};

const toString = (value: unknown): string | undefined => {
  if (typeof value === 'undefined' || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && 'toString' in value) {
    return value.toString();
  }

  return undefined;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'undefined' || value === null) {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    return parseInt(value, 10);
  }

  if (typeof value === 'object' && 'toString' in value) {
    return parseInt(value.toString(), 10);
  }

  return undefined;
};

const toBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'undefined' || value === null) {
    return undefined;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (
    typeof value === 'string' ||
    (typeof value === 'number' &&
      typeof value === 'object' &&
      'toString' in value)
  ) {
    const asString = value.toString();

    return asString === '1' || asString === 'true' || asString === 'on';
  }

  return undefined;
};

const toDateTimeString = (value: unknown): string | undefined => {
  const dateString = toString(value);

  if (typeof dateString === 'undefined') {
    return undefined;
  }

  const dateTime = DateTime.fromJSDate(new Date(dateString));

  if (!dateTime.isValid) {
    return undefined;
  }

  return dateTime.toISO();
};

// Transport state query function
export const getTransportState = (params: TransportState): TransportResult => {
  const key = `${!!params.flightVariant}-${!!params.bus}-${params.trainVariant}`;
  return transportStates[key] || { state: 'unknown' };
};
